"use client";

import { c as _c } from "react/compiler-runtime";
import classNames from "classnames";
import { format } from "date-fns";
import Ad from "src/components/ads/Ad";
import withWatchlist from "src/components/filmwebid/withWatchlist";
import NoShows from "src/components/program/NoShows";
import ProgrammedMovie from "src/components/program/ProgrammedMovie";
import { useDispatchEventOnChange } from "src/lib/ads/useDispatchEventOnChange";
import { useLocationContext, useProgramDatesContext } from "src/lib/contexts/ContextTypes";
import useSsrSafeMediaQuery from "src/lib/client/useSsrSafeMediaQuery";
import type { ProgramMovie } from "src/lib/types/filminfo/ProgramMovie";
import styles from './PosterGrid.module.css';
import getThemeStyles from "src/lib/styling/getThemeStyles";
import type { PosterGridAdConfig } from "src/components/program/PosterGridAdConfig";

//#region [Props]

type PosterGridProps = {
  movies?: ProgramMovie[];
  hideTheatreName?: boolean; // theatre name should not be shown in links
  applyTheatreFilter?: boolean; // film links should pass on a theatre filter
  adConfig?: PosterGridAdConfig;
  numFilteredMovies?: number;
  numFilteredShows?: number;
};
//#endregion

//#region [Component]
export default function PosterGrid(t0) {
  const $ = _c(23);
  const {
    movies,
    hideTheatreName: t1,
    applyTheatreFilter: t2,
    adConfig,
    numFilteredMovies,
    numFilteredShows
  } = t0;
  const hideTheatreName = t1 === undefined ? false : t1;
  const applyTheatreFilter = t2 === undefined ? false : t2;
  const isDesktop = useSsrSafeMediaQuery("(min-width: 550px)");
  const isAtLeastTripleColumn = useSsrSafeMediaQuery("(min-width: 900px)");
  const isQuadColumn = useSsrSafeMediaQuery("(min-width: 1400px)");
  const desktopColumns = isQuadColumn ? 4 : isAtLeastTripleColumn ? 3 : 2;
  const locationContext = useLocationContext();
  const {
    selectedDate,
    showingAllDates
  } = useProgramDatesContext();
  let t3;
  if ($[0] !== selectedDate || $[1] !== showingAllDates) {
    t3 = showingAllDates ? "all" : selectedDate instanceof Date ? format(selectedDate, "yyyy-MM-dd") : selectedDate ? String(selectedDate) : "";
    $[0] = selectedDate;
    $[1] = showingAllDates;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const datePart = t3;
  const location = locationContext?.location ?? "";
  const adKey = `${datePart}|${location}`;
  let t4;
  if ($[3] !== movies) {
    t4 = {
      movies
    };
    $[3] = movies;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  useDispatchEventOnChange("adtargets:ready", t4);
  if ((movies?.length ?? 0) === 0 && !locationContext.location) {
    return null;
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    const themeStyles = getThemeStyles(styles);
    t5 = classNames(styles.SProgramContainer, themeStyles);
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== movies?.length || $[7] !== numFilteredMovies || $[8] !== numFilteredShows) {
    t6 = (movies?.length ?? 0) === 0 && <NoShows className={styles.SNoShows} numFilteredMovies={numFilteredMovies} numFilteredShows={numFilteredShows} />;
    $[6] = movies?.length;
    $[7] = numFilteredMovies;
    $[8] = numFilteredShows;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== adConfig?.desktop || $[11] !== adConfig?.mobile || $[12] !== adKey || $[13] !== applyTheatreFilter || $[14] !== desktopColumns || $[15] !== hideTheatreName || $[16] !== isDesktop || $[17] !== movies) {
    t7 = (movies?.length ?? 0) > 0 && <>{!isDesktop && <><WLProgrammedMovie movie={movies[0]} hideTheatreName={hideTheatreName} applyTheatreFilter={applyTheatreFilter} wlcClassName={styles.WLCProgrammedMovie} wlcButtonClassName={styles.WLButton} wlcStyle={{
          order: 0
        }} />{movies.length > 1 && movies.slice(1).map((movie, index) => <WLProgrammedMovie key={`g2_${index}`} movie={movie} wlcStyle={{
          order: index + 1
        }} wlcButtonClassName={styles.WLButton} hideTheatreName={hideTheatreName} applyTheatreFilter={applyTheatreFilter} wlcClassName={styles.WLCProgrammedMovie} />)}{adConfig?.mobile.map(placement => {
          if (placement.minMovies !== undefined && movies.length <= placement.minMovies) {
            return null;
          }
          const order = placement.order === "last" ? movies.length : placement.order;
          return <Ad key={placement.targetId} className={styles.SAd} targetId={placement.targetId} refreshKey={adKey || ""} style={{
            order
          }} removeIfNoAd={placement.removeIfNoAd} supportsFixedAd={placement.supportsFixedAd} />;
        })}</>}{isDesktop && <>{movies.slice(0, 4).map((movie_0, index_0) => <WLProgrammedMovie key={`g1_${index_0}`} movie={movie_0} wlcButtonClassName={styles.WLButton} wlcStyle={{
          order: index_0
        }} hideTheatreName={hideTheatreName} applyTheatreFilter={applyTheatreFilter} />)}{movies.length > 4 && movies.slice(4).map((movie_1, index_1) => <WLProgrammedMovie key={`g2_${index_1}`} movie={movie_1} wlcButtonClassName={styles.WLButton} wlcStyle={{
          order: index_1 + 4
        }} hideTheatreName={hideTheatreName} applyTheatreFilter={applyTheatreFilter} />)}{adConfig?.desktop.map(placement_0 => {
          if (placement_0.minMovies !== undefined && movies.length <= placement_0.minMovies) {
            return null;
          }
          const order_0 = placement_0.order === "last" ? movies.length : fillRowOrder(placement_0.order, desktopColumns);
          return <Ad key={placement_0.targetId} className={styles.SAd} targetId={placement_0.targetId} refreshKey={adKey || ""} style={{
            order: order_0
          }} removeIfNoAd={placement_0.removeIfNoAd} supportsFixedAd={placement_0.supportsFixedAd} />;
        })}</>}</>;
    $[10] = adConfig?.desktop;
    $[11] = adConfig?.mobile;
    $[12] = adKey;
    $[13] = applyTheatreFilter;
    $[14] = desktopColumns;
    $[15] = hideTheatreName;
    $[16] = isDesktop;
    $[17] = movies;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t5 || $[20] !== t6 || $[21] !== t7) {
    t8 = <div className={t5}>{t6}{t7}</div>;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}
//#endregion

const WLProgrammedMovie = withWatchlist(ProgrammedMovie, "movie", {
  iconVersion: "gradient"
});

// Rounds a configured ad `order` up to the end of the poster row it falls in, so the ad
// never lands mid-row and leaves the rest of that row empty at the current column count.
function fillRowOrder(order: number, columns: number): number {
  return Math.ceil((order + 1) / columns) * columns - 1;
}